import { useIntl } from "react-intl";
import { get, useFormContext, useWatch } from "react-hook-form";

import type { InvitedModes } from "api/models/draftRequest.model";
import Checkbox from "components/v2/Checkbox/Checkbox";
import Divider from "components/v2/Divider/Divider";
import Select from "components/v2/Select/Select";
import FieldErrorBadge from "@RequestFormV2/components/Badges/FieldErrorBadge";
import FormSectionLabel from "pages/shipper/RequestFormV2/components/FormSectionLabel/FormSectionLabel";
import {
  INVITED_MODES_ERROR_PATH,
  ROAD_FREIGHT_OPTION_ERROR_PATH,
} from "@RequestFormV2/validation/sections/validateService";
import { getSelectedInvitedModeValues, isInvitedModeSelected } from "@RequestFormV2/utils/invitedModesHelpers";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";
import { useEditShipmentLock } from "@RequestFormV2/context/editShipmentLock";
import { useService } from "@RequestFormV2/sections/Service/context";

export default function FreightModeSection() {
  const intl = useIntl();
  const isEditShipmentLock = useEditShipmentLock();
  const {
    formState: { errors },
  } = useFormContext();
  const { freightModeEntries, freightModesConfig, handleToggleMode, handleSelectAllType, handleRoadFreightOptionChange } =
    useService();
  const invitedModes = useWatch({ name: "invited_modes" }) as InvitedModes | undefined;
  const availableModes = freightModesConfig?.available_modes ?? [];
  const freightModeError = get(errors, INVITED_MODES_ERROR_PATH);
  const roadFreightOptionError = get(errors, ROAD_FREIGHT_OPTION_ERROR_PATH);

  useReactiveReviewClear(
    INVITED_MODES_ERROR_PATH,
    () => getSelectedInvitedModeValues(invitedModes).length > 0,
    [invitedModes],
  );

  useReactiveReviewClear(
    ROAD_FREIGHT_OPTION_ERROR_PATH,
    () => invitedModes?.road_freight !== true || Boolean(invitedModes?.road_freight_option),
    [invitedModes],
  );

  if (freightModeEntries.length === 0) return null;

  return (
    <div className="rfv2-form-section">
      <FormSectionLabel label={intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight mode" })} required />
      <div className="rfv2-form-section__content">
        <div className="rfv2-freight-modes">
          {freightModeEntries.map(({ key, modes, label, Icon, selectableValues }) => {
            const allSelected =
              selectableValues.length > 0 &&
              selectableValues.every((v) => isInvitedModeSelected(invitedModes, v, availableModes));
            const someSelected = selectableValues.some((v) => isInvitedModeSelected(invitedModes, v, availableModes));

            return (
              <div key={key} className={`rfv2-freight-modes__type-column rfv2-freight-modes__type-column--${key}`}>
                <div className="rfv2-freight-modes__type-header">
                  <Icon />
                  <label>{label}</label>
                </div>

                <div className="rfv2-freight-modes__modes">
                  {modes.length > 1 && (
                    <div>
                      <Checkbox
                        label={intl.formatMessage({ id: "general.selectAll", defaultMessage: "Select all" })}
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        disabled={isEditShipmentLock}
                        onChange={() => {
                          if (isEditShipmentLock) return;
                          handleSelectAllType(selectableValues, allSelected);
                        }}
                      />
                      <Divider />
                    </div>
                  )}

                  {modes.map((mode) =>
                    mode.options?.length ? (
                      <div key={mode.value}>
                        <Checkbox
                          label={mode.label}
                          checked={invitedModes?.[mode.value] === true}
                          disabled={isEditShipmentLock}
                          onChange={() => {
                            if (isEditShipmentLock) return;
                            handleToggleMode(mode.value);
                          }}
                        />
                        {invitedModes?.[mode.value] === true && (
                          <div className="ml-3xl">
                            <Select
                              options={mode.options}
                              value={(invitedModes?.road_freight_option as string) ?? ""}
                              onChange={(value) => {
                                if (isEditShipmentLock) return;
                                handleRoadFreightOptionChange(value);
                              }}
                              saveOnChange={false}
                              isClearable={!isEditShipmentLock}
                              isDisabled={isEditShipmentLock}
                              required
                              hasError={Boolean(roadFreightOptionError)}
                              errorMessage={
                                roadFreightOptionError?.message ? String(roadFreightOptionError.message) : undefined
                              }
                              placeholder={intl.formatMessage({
                                id: "request-form.service.freightMode.selectType",
                                defaultMessage: "Select type",
                              })}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <Checkbox
                        key={mode.value}
                        label={mode.label}
                        checked={isInvitedModeSelected(invitedModes, mode.value, availableModes)}
                        disabled={isEditShipmentLock}
                        onChange={() => {
                          if (isEditShipmentLock) return;
                          handleToggleMode(mode.value);
                        }}
                      />
                    ),
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {freightModeError?.message && <FieldErrorBadge message={String(freightModeError.message)} />}
      </div>
    </div>
  );
}
